import React, { useId } from 'react';
import { normalizedContains, sortByAlphabet } from 'shared/utils';
import styles from './forms.module.scss';
import { ConfigProvider, Select, Tag } from 'antd';
import { colorPalette, antdTheme as theme } from 'shared/constants';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleX } from '@fortawesome/pro-solid-svg-icons';

// Errors aren't always strings — yup yields `{ message }` objects and per-item
// arrays, and rendering either as a React child unmounts the tree.
export const toErrorText = error => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error.map(toErrorText).filter(Boolean).join(', ');
  return typeof error.message === 'string' ? error.message : '';
};

export function SelectBox({
  label = null,
  value,
  options,
  onChange,
  name = '',
  placeholder = '',
  error = null,
  hideErrorText = false,
  disabled = false,
  allowClear = false,
  required = false,
  sortOptions = true,
  id,
  ...props
}) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const errorText = toErrorText(error);

  const handleOnChange = value => {
    onChange(name, value);
  };

  // Copy before sorting so we don't mutate the caller's array.
  const sortedOptions = sortOptions
    ? [...(options || [])].sort((a, b) => sortByAlphabet(a?.label, b?.label))
    : options || [];

  const tagRender = props => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = event => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ margin: 3, whiteSpace: 'pre-wrap' }}>
        {label || value}
      </Tag>
    );
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          // Global tokens for icons (affects Tag close icon color)
          colorIcon: 'rgba(255, 255, 255, 0.85)', // White-ish close icon
          colorIconHover: 'rgba(255, 255, 255, 1)', // White close icon on hover
        },
        components: {
          Select: {
            borderRadius: 8,
            controlHeight: 48,
            colorBorder: theme.colorBorder,
            colorTextPlaceholder: theme.colorTextPlaceholder,
          },
          Tag: {
            defaultBg: colorPalette.primaryMain1,
            colorBorder: colorPalette.primaryMain5,
            lineHeightSM: 2,
          },
        },
      }}>
      <div className={styles.root}>
        {label && <label htmlFor={inputId}>{label}</label>}
        <Select
          id={inputId}
          value={!_.isEmpty(value) ? value : undefined}
          status={errorText && 'error'}
          placeholder={disabled ? '' : placeholder}
          name={name}
          onChange={handleOnChange}
          filterOption={(inputValue, option) => normalizedContains(option?.label, inputValue)}
          disabled={disabled}
          showSearch
          allowClear={
            allowClear
              ? {
                  clearIcon: <FontAwesomeIcon icon={faCircleX} size="md" />,
                }
              : false
          }
          tagRender={tagRender}
          aria-required={required}
          options={sortedOptions}
          {...props}
        />
        {!hideErrorText && <span className={styles.error}>{errorText}</span>}
      </div>
    </ConfigProvider>
  );
}
